import { useMemo } from 'react';
import { Form } from 'antd';
import dayjs from 'dayjs';
import { isNonEmploymentTypeValue } from '@/shared/employee';
import { getFieldState } from '@/utils/formUtils';

/**
 * Keys, permission states and watched values of the employment and annual leave fields, and the employment change check.
 */
export default function useEmployeeEmploymentFields({
  INPUT_KEYS,
  form,
  formData,
  formFields,
  isEdited,
}) {
  // * Employment Settings form data
  const workTimeCustomTypeKeys =
    INPUT_KEYS.employmentSettings.workTimeCustomType;
  const workTimeCustomTypeState = getFieldState({
    formFields,
    formKeys: workTimeCustomTypeKeys,
    isEdited,
  });

  const workTimeTypeKeys = INPUT_KEYS.employmentSettings.workTimeType;
  const workTimeTypeState = getFieldState({
    formFields,
    formKeys: workTimeTypeKeys,
    isEdited,
  });

  const employmentTypeKeys = INPUT_KEYS.employmentSettings.employmentType;
  const employmentTypeState = getFieldState({
    formFields,
    formKeys: employmentTypeKeys,
    isEdited,
  });

  const isHrOfficerKeys = INPUT_KEYS.employmentSettings.isHrOfficer;
  const isHrOfficerState = getFieldState({
    formFields,
    formKeys: isHrOfficerKeys,
    isEdited,
  });

  const shiftWorkerKeys = INPUT_KEYS.employmentSettings.shiftWorker;
  const shiftWorkerState = getFieldState({
    formFields,
    formKeys: shiftWorkerKeys,
    isEdited,
  });

  const bonusBasisKeys = INPUT_KEYS.otherAllowances.bonusBasis;
  const bonusBasisState = getFieldState({
    formFields,
    formKeys: bonusBasisKeys,
    isEdited,
  });

  const startDateKeys = INPUT_KEYS.employmentSettings.startDate;
  const startDateState = getFieldState({
    formFields,
    formKeys: startDateKeys,
    isEdited,
  });

  const endDateKeys = INPUT_KEYS.employmentSettings.endDate;
  const endDateState = getFieldState({
    formFields,
    formKeys: endDateKeys,
    isEdited,
  });

  const dateOfWorkPermissionForeignerKeys =
    INPUT_KEYS.employmentSettings.dateOfWorkPermissionForeigner;
  const dateOfWorkPermissionForeignerState = getFieldState({
    formFields,
    formKeys: dateOfWorkPermissionForeignerKeys,
    isEdited,
  });

  const workTimeCustomType = Form.useWatch(workTimeCustomTypeKeys, form);
  const workTimeType = Form.useWatch(workTimeTypeKeys, form);
  const employmentType = Form.useWatch(employmentTypeKeys, form);
  const startDate = Form.useWatch(startDateKeys, form);
  const endDate = Form.useWatch(endDateKeys, form);

  // Track initial employment-related values to detect when the user is making
  // a contract change (extend / shorten / convert / re-time). When detected,
  // we show an `effectiveDate` field so the HR can record the business date
  // separate from the system timestamp.
  const initialEmploymentRef = useMemo(
    () => ({
      employmentType: formData?.employmentSettings?.employmentType ?? null,
      endDate: formData?.employmentSettings?.endDate ?? null,
      startDate: formData?.employmentSettings?.startDate ?? null,
      workTimeType: formData?.employmentSettings?.workTimeType ?? null,
    }),
    [
      formData?.employmentSettings?.employmentType,
      formData?.employmentSettings?.endDate,
      formData?.employmentSettings?.startDate,
      formData?.employmentSettings?.workTimeType,
    ]
  );

  const sameDay = (a, b) => {
    if (!a && !b) return true;
    if (!a || !b) return false;
    return dayjs(a).format('YYYY-MM-DD') === dayjs(b).format('YYYY-MM-DD');
  };

  const employmentChangeDetected =
    isEdited &&
    ((employmentType ?? null) !== initialEmploymentRef.employmentType ||
      !sameDay(endDate, initialEmploymentRef.endDate) ||
      !sameDay(startDate, initialEmploymentRef.startDate) ||
      (workTimeType ?? null) !== initialEmploymentRef.workTimeType);

  // * Holiday Leave Calculation form data
  const employeeTrackerTypeKeys =
    INPUT_KEYS.holidayLeaveCalculation.employeeTrackerType;
  const employeeTrackerTypeState = getFieldState({
    formFields,
    formKeys: employeeTrackerTypeKeys,
    isEdited,
  });

  const numberOfHolidayDaysContractKeys =
    INPUT_KEYS.holidayLeaveCalculation.numberOfHolidayDaysContract;
  const numberOfHolidayDaysContractState = getFieldState({
    formFields,
    formKeys: numberOfHolidayDaysContractKeys,
    isEdited,
  });

  const numberOfHolidayDaysPastKeys =
    INPUT_KEYS.holidayLeaveCalculation.numberOfHolidayDaysPast;
  const numberOfHolidayDaysPastState = getFieldState({
    formFields,
    formKeys: numberOfHolidayDaysPastKeys,
    isEdited,
  });

  const numberOfHolidayDaysUsedKeys =
    INPUT_KEYS.holidayLeaveCalculation.numberOfHolidayDaysUsed;
  const numberOfHolidayDaysUsedState = getFieldState({
    formFields,
    formKeys: numberOfHolidayDaysUsedKeys,
    isEdited,
  });

  const employeeTrackerType = Form.useWatch(employeeTrackerTypeKeys, form);
  const numberOfHolidayDaysContract = Form.useWatch(
    numberOfHolidayDaysContractKeys,
    form
  );
  const numberOfHolidayDaysPast = Form.useWatch(
    numberOfHolidayDaysPastKeys,
    form
  );
  const numberOfHolidayDaysUsed = Form.useWatch(
    numberOfHolidayDaysUsedKeys,
    form
  );

  // * Variables
  const isSelectedEmployment = !!startDate || !!endDate;
  const isExistingUser = employeeTrackerType === 'existing' || isEdited;
  const isLimitedEmployment = employmentType === 'limited';
  // Non-employment contracts (service contract / author / supplementary / PP) —
  // show the end date (they have a term), but do NOT show the annual-leave
  // calculation (they have none).
  const isNonEmployment = isNonEmploymentTypeValue(employmentType);
  const showEndDate = isLimitedEmployment || isNonEmployment;

  return {
    bonusBasisKeys,
    bonusBasisState,
    dateOfWorkPermissionForeignerKeys,
    dateOfWorkPermissionForeignerState,
    employeeTrackerType,
    employeeTrackerTypeKeys,
    employeeTrackerTypeState,
    employmentChangeDetected,
    employmentType,
    employmentTypeKeys,
    employmentTypeState,
    endDate,
    endDateKeys,
    endDateState,
    isExistingUser,
    isHrOfficerKeys,
    isHrOfficerState,
    isNonEmployment,
    isSelectedEmployment,
    numberOfHolidayDaysContract,
    numberOfHolidayDaysContractKeys,
    numberOfHolidayDaysContractState,
    numberOfHolidayDaysPast,
    numberOfHolidayDaysPastKeys,
    numberOfHolidayDaysPastState,
    numberOfHolidayDaysUsed,
    numberOfHolidayDaysUsedKeys,
    numberOfHolidayDaysUsedState,
    shiftWorkerKeys,
    shiftWorkerState,
    showEndDate,
    startDate,
    startDateKeys,
    startDateState,
    workTimeCustomType,
    workTimeCustomTypeKeys,
    workTimeCustomTypeState,
    workTimeType,
    workTimeTypeKeys,
    workTimeTypeState,
  };
}
